import { clsx } from 'clsx';
import { ChevronDown, ExternalLink, Loader2, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ByokProviderDefinition } from '@/components/settings/byok-provider-catalog';
import { Input } from '@/components/ui/Input';

type ByokProviderCardProps = {
  provider: ByokProviderDefinition;
  expanded: boolean;
  onToggleExpand: () => void;
  /** Saved key active for this provider (visual highlight only). */
  active?: boolean;
  /** Saved key exists for this provider. */
  hasSavedKey?: boolean;
  enabled?: boolean;
  toggleBusy?: boolean;
  onToggleEnabled?: (enabled: boolean) => void;
  canEdit: boolean;
  apiKey: string;
  onApiKeyChange: (value: string) => void;
  endpoint: string;
  onEndpointChange: (value: string) => void;
  selectedModel: string;
  onModelChange: (modelId: string) => void;
  keyLast4?: string | null;
  busy: 'test' | 'save' | 'delete' | 'toggle' | null;
  error: string | null;
  testResult: { ok: boolean; embed?: boolean; chat?: boolean } | null;
  onTest: () => void;
  onSave: () => void;
  onDelete: () => void;
};

export function ByokProviderCard({
  provider,
  expanded,
  onToggleExpand,
  active = false,
  hasSavedKey = false,
  enabled = false,
  toggleBusy = false,
  onToggleEnabled,
  canEdit,
  apiKey,
  onApiKeyChange,
  endpoint,
  onEndpointChange,
  selectedModel,
  onModelChange,
  keyLast4,
  busy,
  error,
  testResult,
  onTest,
  onSave,
  onDelete,
}: ByokProviderCardProps) {
  const { t } = useTranslation('settings');
  const name = t(provider.nameKey, provider.nameFallback);
  const description = t(provider.descriptionKey, provider.descriptionFallback);
  const keyLinkLabel = t(provider.keyUrlKey, provider.keyUrlFallback);

  return (
    <div
      className={clsx(
        'overflow-hidden rounded-2xl border bg-card transition-shadow',
        active && provider.available ? 'border-primary/40 shadow-sm' : 'border-border'
      )}
    >
      <div className="flex items-start gap-3 p-4 sm:p-5">
        <span
          className={clsx(
            'mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-sm font-bold',
            provider.badgeClassName
          )}
          aria-hidden
        >
          {name.slice(0, 1)}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-bold text-foreground">{name}</h2>
            {!provider.available ? (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('aiByok.comingSoon')}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{description}</p>
          <a
            href={provider.keyUrlHref}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            {keyLinkLabel}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
        </div>

        {provider.available && canEdit ? (
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-label={t('aiByok.providerEnable', { provider: name })}
            title={!hasSavedKey ? t('aiByok.enableToggleHint') : undefined}
            disabled={!hasSavedKey || toggleBusy || busy !== null}
            onClick={(e) => {
              e.stopPropagation();
              onToggleEnabled?.(!enabled);
            }}
            className={clsx(
              'relative mt-1 h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50',
              enabled ? 'bg-primary' : 'bg-muted-foreground/30'
            )}
          >
            <span
              aria-hidden
              className={clsx(
                'absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
                enabled ? 'translate-x-5' : 'translate-x-0',
                toggleBusy && 'opacity-70'
              )}
            />
          </button>
        ) : null}

        <button
          type="button"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          aria-label={t('aiByok.expandProvider', { provider: name })}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-muted-foreground hover:bg-muted/60"
        >
          <ChevronDown
            className={clsx('h-5 w-5 transition-transform', expanded && 'rotate-180 text-primary')}
            aria-hidden
          />
        </button>
      </div>

      <div
        className={clsx(
          'overflow-hidden border-t border-border-muted transition-all duration-300',
          expanded ? 'max-h-[1200px] opacity-100' : 'max-h-0 opacity-0 border-t-0'
        )}
      >
        <div className="space-y-4 px-4 pb-5 pt-4 sm:px-5">
          {!provider.available ? (
            <p className="text-sm text-muted-foreground">{t('aiByok.comingSoonHint')}</p>
          ) : canEdit ? (
            <>
              {keyLast4 ? (
                <p className="text-xs text-muted-foreground">
                  {t('aiByok.savedKeyHint', { last4: keyLast4 })}
                </p>
              ) : null}

              <Input
                id={`byok-key-${provider.id}`}
                label={t('aiByok.apiKeyLabel')}
                placeholder={t('aiByok.apiKeyPlaceholder')}
                autoComplete="off"
                showPasswordToggle
                value={apiKey}
                onChange={(e) => onApiKeyChange(e.target.value)}
              />
              <p className="-mt-2 text-xs text-muted-foreground">{t('aiByok.apiKeyHint')}</p>

              <Input
                id={`byok-endpoint-${provider.id}`}
                label={t('aiByok.proxyLabel')}
                placeholder={provider.defaultBaseUrl}
                value={endpoint}
                onChange={(e) => onEndpointChange(e.target.value)}
              />
              <p className="-mt-2 text-xs text-muted-foreground">{t('aiByok.proxyHint')}</p>

              {provider.models && provider.models.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-foreground">{t('aiByok.selectModel')}</p>
                  <div className="flex flex-wrap gap-2">
                    {provider.models.map((model) => {
                      const selected = selectedModel === model.id;
                      return (
                        <button
                          key={model.id}
                          type="button"
                          onClick={() => onModelChange(model.id)}
                          className={clsx(
                            'rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                            selected
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border bg-background text-muted-foreground hover:border-primary/40'
                          )}
                        >
                          {t(model.labelKey, model.labelFallback)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {testResult ? (
                <p className="text-xs text-muted-foreground">
                  {testResult.ok
                    ? t('aiByok.testOk')
                    : t('aiByok.testChecks', { embed: testResult.embed, chat: testResult.chat })}
                </p>
              ) : null}
              {error ? <p className="text-sm text-destructive">{error}</p> : null}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={!apiKey.trim() || busy !== null}
                  onClick={onTest}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50"
                >
                  {busy === 'test' ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      {t('aiByok.testing')}
                    </span>
                  ) : (
                    t('aiByok.testConnection')
                  )}
                </button>
                <button
                  type="button"
                  disabled={!apiKey.trim() || busy !== null}
                  onClick={onSave}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                >
                  {busy === 'save' ? t('aiByok.saving') : t('aiByok.saveKey')}
                </button>
              </div>

              {keyLast4 ? (
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={onDelete}
                  className="flex items-center gap-2 text-sm font-medium text-destructive hover:underline disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                  {t('aiByok.deleteKey')}
                </button>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t('aiByok.memberReadOnly')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
